// Segmented (audit §4b): one frame divided by hairlines, the segment that's on filled in ink
// (DESIGN.md §8). Two kinds, one drawing:
// - a choice of one (the row switch: comfortable / compact): selectionMode 'single', a radio-like group;
// - a choice of several (the working days, the ways to sign in): selectionMode 'multiple', each
//   segment a toggle that's pressed or not;
// - a row of actions (‹ Today ›): selectionMode 'none', each segment a button, and `on` marks the one
//   that describes what's shown (Today, when the board shows today).
// A segment is text, or a glyph with a label for screen readers. Reads only --seg-*.
import type { Key } from 'react-aria-components';
import { Button as AriaButton, ToggleButton, ToggleButtonGroup } from 'react-aria-components';
import { Glyph, type GlyphName } from '../Glyph/Glyph.tsx';
import './Segmented.css';

export interface Segment {
  id: string;
  /** The words, or the screen-reader label when the segment is a glyph. */
  label: string;
  icon?: GlyphName;
  isDisabled?: boolean;
}

interface Common {
  /** What the group is, for screen readers ("Rows", "Day"). */
  label: string;
  items: Segment[];
}
export type SegmentedProps =
  | (Common & {
      selectionMode?: 'single';
      selectedKey: string;
      onSelectionChange?: (id: string) => void;
    })
  | (Common & {
      selectionMode: 'multiple';
      selectedKeys: readonly string[];
      onSelectionChange?: (ids: string[]) => void;
    })
  | (Common & {
      selectionMode: 'none';
      onAction?: (id: string) => void;
      /** The segment that describes what's shown, drawn on (Today). */
      on?: string;
    });

function Face({ item }: { item: Segment }) {
  return item.icon ? (
    <Glyph name={item.icon} className="fl-seg__glyph" />
  ) : (
    <span className="fl-seg__label">{item.label}</span>
  );
}

export function Segmented(props: SegmentedProps) {
  if (props.selectionMode === 'none') {
    const { label, items, onAction, on } = props;
    return (
      <div role="group" aria-label={label} className="fl-seg">
        {items.map((item) => (
          <AriaButton
            key={item.id}
            className="fl-seg__item"
            data-icon={item.icon ? '' : undefined}
            data-on={on === item.id ? '' : undefined}
            aria-current={on === item.id ? 'true' : undefined}
            aria-label={item.icon ? item.label : undefined}
            isDisabled={item.isDisabled}
            onPress={() => onAction?.(item.id)}
          >
            <Face item={item} />
          </AriaButton>
        ))}
      </div>
    );
  }
  const toggles = props.items.map((item) => (
    <ToggleButton
      key={item.id}
      id={item.id}
      className="fl-seg__item"
      data-icon={item.icon ? '' : undefined}
      aria-label={item.icon ? item.label : undefined}
      isDisabled={item.isDisabled}
    >
      <Face item={item} />
    </ToggleButton>
  ));
  if (props.selectionMode === 'multiple') {
    const { label, selectedKeys, onSelectionChange } = props;
    return (
      <ToggleButtonGroup
        aria-label={label}
        className="fl-seg"
        selectionMode="multiple"
        selectedKeys={selectedKeys}
        onSelectionChange={(keys: Set<Key>) => onSelectionChange?.([...keys].map(String))}
      >
        {toggles}
      </ToggleButtonGroup>
    );
  }
  const { label, selectedKey, onSelectionChange } = props;
  return (
    <ToggleButtonGroup
      aria-label={label}
      className="fl-seg"
      selectionMode="single"
      disallowEmptySelection
      selectedKeys={[selectedKey]}
      onSelectionChange={(keys: Set<Key>) => {
        const [k] = [...keys];
        if (k !== undefined) onSelectionChange?.(String(k));
      }}
    >
      {toggles}
    </ToggleButtonGroup>
  );
}
